import Head from 'next/head';
import Link from 'next/link';
import dayjs from 'dayjs';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { PortableText } from '@portabletext/react';

import { buildArticlePortableTextComponents } from '@components/article/portable-text-components';
import { ShareRow } from '@components/article/ShareRow';
import {
  CallToActionLink,
  toCompleteCallToAction
} from '@components/whats-new/CallToActionLink';
import { CategoryLabel } from '@components/whats-new/CategoryLabel';
import { PlatformTag } from '@components/whats-new/PlatformTag';
import {
  SCREENSHOT_PANEL_CLASS_NAME,
  Screenshot
} from '@components/whats-new/Screenshot';
import { ScreenshotsBlock } from '@components/whats-new/ScreenshotsBlock';
import { UPDATE_DATE_FORMAT } from '@components/whats-new/UpdateCard';
import {
  BETA_BUTTON_CLASS_NAME,
  PRIMARY_BUTTON_CLASS_NAME,
  SITE_HEADING_CLASS_NAME,
  TEXT_LINK_CLASS_NAME
} from '@components/whats-new/styles';
import { MissingPropsRecovery } from '@components/missing-props-recovery';
import { extractHeadings } from '@lib/article-blocks/headings';
import { hydrateArticleBlocks } from '@lib/article-blocks/hydrate';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import { scaleShareImageDimensions } from '@lib/article-seo';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { sanityClient } from '@lib/sanity/client';
import { buildSanityImageUrl } from '@lib/sanity/image';
import {
  BETA_CATEGORY,
  WHATS_NEW_PATH,
  allProductUpdateSlugsQuery,
  buildProductUpdatePath,
  getProductUpdate,
  getProductUpdates,
  isProductUpdateCategory
} from '@lib/sanity/productUpdate';
import type {
  SanityProductUpdate,
  SanityProductUpdateSummary
} from '@lib/sanity/types';
import {
  FEEDBACK_SECTION_ID,
  WHATS_NEW_FEEDBACK_FORM_ID
} from '@lib/whats-new/feedback';
import { findNeighbours, updateDate } from '@lib/whats-new/model';
import type { NeighbourUpdate } from '@lib/whats-new/model';

export const REVALIDATE_SECONDS = 600;
// An unknown slug can be a publish race, so the 404 must expire rather than
// cache until the next deploy.
export const NOT_FOUND_REVALIDATE_SECONDS = 60;

const OG_IMAGE_WIDTH = 1200;
const HERO_IMAGE_WIDTHS = [480, 640, 960, 1280];
const HERO_IMAGE_SIZES = {
  phone: '250px',
  wide: '(min-width: 52rem) 680px, 90vw'
};
const HERO_IMAGE_DEFAULT_WIDTH = 960;

const PROPS_RELOAD_MARKER_KEY = 'ffh-product-update-props-reload';
const MISSING_PROPS_RUM_CONTEXT = 'product-update-missing-props';

export type ProductUpdatePageProps = {
  /** See ArticlePage: a failed client-side data fetch can arrive without it. */
  update?: SanityProductUpdate;
  blockData: ArticleBlockData;
  neighbours: { newer: NeighbourUpdate | null; older: NeighbourUpdate | null };
  isPreview?: boolean;
};

export const buildProductUpdatePageData = (
  update: SanityProductUpdate,
  updates: SanityProductUpdateSummary[]
): Pick<ProductUpdatePageProps, 'neighbours'> => {
  // A draft is not in the published list yet, so the preview places it.
  const withThisUpdate = updates.some(({ slug }) => slug === update.slug)
    ? updates
    : [update, ...updates];
  return { neighbours: findNeighbours(withThisUpdate, update.slug) };
};

const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M10 3L5 8l5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NeighbourLink = ({
  neighbour,
  heading,
  isAlignedEnd = false
}: {
  neighbour: NeighbourUpdate;
  heading: string;
  isAlignedEnd?: boolean;
}) => (
  <Link
    href={buildProductUpdatePath(neighbour.slug)}
    className={`flex flex-col gap-1.5 text-landing-ink no-underline hover:underline ${
      isAlignedEnd ? 'sm:text-right' : ''
    }`}
  >
    <span className="font-body text-[13px] font-bold text-landing-slate">
      {heading}, {dayjs(neighbour.date).format(UPDATE_DATE_FORMAT)}
    </span>
    <span className="font-heading text-lg font-extrabold leading-[1.35]">
      {neighbour.title}
    </span>
  </Link>
);

export const ProductUpdatePage = ({
  update,
  blockData,
  neighbours,
  isPreview = false
}: ProductUpdatePageProps) => {
  if (!update) {
    return (
      <MissingPropsRecovery
        context={MISSING_PROPS_RUM_CONTEXT}
        reloadMarkerKey={PROPS_RELOAD_MARKER_KEY}
        fallbackPath={WHATS_NEW_PATH}
        errorMessage="Product update page props missing"
      />
    );
  }

  const category = isProductUpdateCategory(update.category)
    ? update.category
    : null;
  const publishedAt = updateDate(update);
  const callToAction = toCompleteCallToAction(update.callToAction);
  const path = buildProductUpdatePath(update.slug);
  const canonicalUrl = update.seo?.canonicalUrl || `${FFH_HOSTNAME}${path}`;
  const metaTitle = update.seo?.metaTitle || `${update.title} | ${SITE_NAME}`;
  const metaDescription = update.seo?.metaDescription || update.standfirst;
  const shareImage = update.seo?.ogImage ?? update.featuredImage;
  const ogImageUrl = shareImage
    ? buildSanityImageUrl(shareImage).width(OG_IMAGE_WIDTH).url()
    : undefined;
  const shareImageDimensions = scaleShareImageDimensions(
    OG_IMAGE_WIDTH,
    shareImage?.asset?.metadata?.dimensions
  );
  const heroImage = update.featuredImage;
  const body = update.body ?? [];
  const articleComponents = buildArticlePortableTextComponents(blockData, [
    ...extractHeadings(body)
  ]);
  const components = {
    ...articleComponents,
    types: { ...articleComponents.types, screenshotsBlock: ScreenshotsBlock }
  };

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        {metaDescription && (
          <meta
            property="og:description"
            content={metaDescription}
            key="og:description"
          />
        )}
        {ogImageUrl && (
          <meta property="og:image" content={ogImageUrl} key="og:image" />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:width"
            content={String(shareImageDimensions.width)}
            key="og:image:width"
          />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:height"
            content={String(shareImageDimensions.height)}
            key="og:image:height"
          />
        )}
        <meta property="og:type" content="article" key="og:type" />
        <meta
          property="article:published_time"
          content={publishedAt}
          key="article:published_time"
        />
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        {(isPreview || update.seo?.isHiddenFromSearch) && (
          <meta name="robots" content="noindex,nofollow" />
        )}
      </Head>
      <div className="bg-white text-landing-ink">
        {isPreview && (
          <div
            className="bg-warning-300 px-4 py-2 text-center font-body text-sm font-semibold text-neutral-900"
            data-cy="product-update-preview-banner"
          >
            Draft preview. Publish in the Studio to make this update live.
            {category === null && ' Choose a category before publishing.'}
          </div>
        )}
        <div className="mx-auto max-w-[824px] px-4 pb-24 pt-8 sm:px-8 sm:pt-11">
          <Link
            href={WHATS_NEW_PATH}
            className="inline-flex items-center gap-1.5 font-heading text-sm font-bold text-primary-700 no-underline hover:underline"
          >
            <ChevronLeftIcon />
            All updates
          </Link>

          <header className="mt-5 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-body text-sm text-landing-slate">
              {category && <CategoryLabel category={category} />}
              <PlatformTag platform={update.platform} />
              <time dateTime={publishedAt}>
                {dayjs(publishedAt).format(UPDATE_DATE_FORMAT)}
              </time>
            </div>
            <h1
              className={`${SITE_HEADING_CLASS_NAME} m-0 text-[2rem] leading-[1.08] text-landing-ink [text-wrap:balance] sm:text-[46px]`}
            >
              {update.title}
            </h1>
            {update.standfirst && (
              <p className="m-0 font-body text-lg leading-[1.55] text-[#33475a] sm:text-[21px]">
                {update.standfirst}
              </p>
            )}
            {callToAction && (
              <div className="pt-1">
                <CallToActionLink
                  callToAction={callToAction}
                  className={
                    category === BETA_CATEGORY
                      ? BETA_BUTTON_CLASS_NAME
                      : PRIMARY_BUTTON_CLASS_NAME
                  }
                />
              </div>
            )}
          </header>

          <article className="article mt-10 min-w-0">
            {heroImage?.asset && (
              <figure className="m-0 mb-10">
                <div
                  className={`flex justify-center rounded-xl px-4 py-8 sm:p-10 ${SCREENSHOT_PANEL_CLASS_NAME}`}
                >
                  <Screenshot
                    image={heroImage}
                    scale="page"
                    widths={HERO_IMAGE_WIDTHS}
                    defaultWidth={HERO_IMAGE_DEFAULT_WIDTH}
                    sizes={HERO_IMAGE_SIZES}
                    maxHeightClassName="[--screenshot-max-height:min(540px,70svh)]"
                    fetchPriority="high"
                  />
                </div>
                {(heroImage.caption || heroImage.credit) && (
                  <figcaption className="mt-2.5 font-body text-[13px] text-landing-slate">
                    {[heroImage.caption, heroImage.credit]
                      .filter(Boolean)
                      .join(', ')}
                  </figcaption>
                )}
              </figure>
            )}
            <div className="content-body">
              <PortableText value={body} components={components} />
            </div>
            <ShareRow url={canonicalUrl} title={update.title} />
            {(neighbours.newer || neighbours.older) && (
              <nav
                aria-label="More updates"
                className="mt-10 grid gap-6 border-t border-landing-hairline pt-7 sm:grid-cols-2"
              >
                <div>
                  {neighbours.older && (
                    <NeighbourLink
                      neighbour={neighbours.older}
                      heading="Earlier"
                    />
                  )}
                </div>
                <div>
                  {neighbours.newer && (
                    <NeighbourLink
                      neighbour={neighbours.newer}
                      heading="Newer"
                      isAlignedEnd
                    />
                  )}
                </div>
              </nav>
            )}
            {WHATS_NEW_FEEDBACK_FORM_ID && (
              <aside className="mt-10 flex flex-col gap-2 rounded-2xl bg-landing-surface p-6">
                <h2 className="m-0 font-heading text-lg font-extrabold text-landing-ink">
                  Got thoughts on this update?
                </h2>
                <p className="m-0 font-body text-[15px] leading-[1.6] text-[#33475a]">
                  Tell us what works, what doesn&apos;t and what you&apos;d like
                  us to build next.
                </p>
                <Link
                  href={`${WHATS_NEW_PATH}#${FEEDBACK_SECTION_ID}`}
                  className={`${TEXT_LINK_CLASS_NAME} self-start`}
                >
                  Send us feedback
                </Link>
              </aside>
            )}
          </article>
        </div>
      </div>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient.fetch<{ slug: string }[]>(
    allProductUpdateSlugsQuery
  );
  return {
    paths: slugs
      .filter((entry) => entry.slug)
      .map((entry) => ({ params: { slug: entry.slug } })),
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps<ProductUpdatePageProps> = async ({
  params
}) => {
  const slug = params?.slug as string;
  const [update, updates] = await Promise.all([
    getProductUpdate(slug),
    getProductUpdates()
  ]);

  if (!update || !isProductUpdateCategory(update.category)) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  return {
    props: {
      update,
      blockData: await hydrateArticleBlocks(update.body ?? []),
      ...buildProductUpdatePageData(update, updates)
    },
    revalidate: REVALIDATE_SECONDS
  };
};

export default ProductUpdatePage;
